/**
 * ImportInvoicesDialog — the invoice column vocabulary and copy, wrapped
 * around the shared `GroupedImportDialog`.
 *
 * CSV shape (one row = one line; group by `source-ref`):
 *   Header (repeated per source-ref, first row wins):
 *     source-ref                 idempotency key (max 60)
 *     customer-id                UUID (from `/sales/customers` list; must exist)
 *     issued-on / due-on         ISO date YYYY-MM-DD
 *     place-of-supply            two-digit GSTIN state code
 *     customer-note              (optional; visible to customer)
 *     internal-note              (optional; internal only)
 *   Line (one per CSV row):
 *     description                free text
 *     hsn                        4-8 char HSN/SAC
 *     unit                       Nos, Pcs, KG, etc. (default Nos)
 *     quantity                   decimal (e.g. 1.5) — converted to milli
 *     rate-rupees                per-unit rate in rupees (e.g. 1200.50)
 *     tax-percent                e.g. 18 → bps 1800
 *     discount-percent           default 0 → bps
 *
 * INR + `taxable_cgst_sgst` treatment by default (V1 sync scope). Foreign-
 * currency + non-standard tax treatments require the composer, not import.
 */
import type { invoices as contract } from "@flint/api-contract";
import {
  commonHeaderFields,
  GroupedImportDialog,
  type GroupedImportConfig,
  type GroupedImportRunner,
  type ImportSummary,
} from "../../../components/csv-import/index.ts";
import { trpc } from "../../../lib/trpc.ts";

type InvoiceHeader = Omit<contract.ImportRow, "lines" | "sourceRef">;

const HEADER_MAP: Record<string, string> = {
  "source-ref": "sourceRef",
  "customer-id": "customerId",
  "issued-on": "issuedOn",
  "due-on": "dueOn",
  "place-of-supply": "placeOfSupply",
  "customer-note": "customerNote",
  "internal-note": "internalNote",
  description: "description",
  hsn: "hsn",
  unit: "unit",
  quantity: "_quantity",
  "rate-rupees": "_rateRupees",
  "tax-percent": "_taxPercent",
  "discount-percent": "_discountPercent",
};

const CONFIG: GroupedImportConfig<InvoiceHeader> = {
  noun: "invoice",
  nounPlural: "invoices",
  article: "an",
  inputId: "import-invoice-csv",
  dialogDescription: (
    <>
      Up to 500 rows per file (grouped into invoices by <code>source-ref</code>). Duplicates and
      validation errors are skipped — valid invoices still land as drafts.
    </>
  ),
  templateHint: (
    <>
      Two example rows show a two-line invoice grouped by <code>IMP-001</code>. Replace{" "}
      <code>{"{customer-uuid}"}</code> with a real customer id from the Customers list. INR +
      CGST/SGST treatment is assumed; foreign currency + special tax treatments require the
      composer.
    </>
  ),
  schema: {
    headerMap: HEADER_MAP,
    headerLabel: {
      "source-ref": "Source ref (invoice group key)",
      "customer-id": "Customer UUID",
      "issued-on": "Issued on (YYYY-MM-DD)",
      "due-on": "Due on (YYYY-MM-DD)",
      "place-of-supply": "Place of supply (state code)",
      "customer-note": "Customer note",
      "internal-note": "Internal note",
      description: "Line description",
      hsn: "HSN/SAC",
      unit: "Unit",
      quantity: "Quantity",
      "rate-rupees": "Rate (₹)",
      "tax-percent": "Tax %",
      "discount-percent": "Discount %",
    },
    headerAliases: {
      "invoice ref": "source-ref",
      reference: "source-ref",
      ref: "source-ref",
      customer: "customer-id",
      "customer uuid": "customer-id",
      "customer id": "customer-id",
      date: "issued-on",
      "invoice date": "issued-on",
      "due date": "due-on",
      "place of supply": "place-of-supply",
      pos: "place-of-supply",
      state: "place-of-supply",
      item: "description",
      product: "description",
      "hsn code": "hsn",
      sac: "hsn",
      qty: "quantity",
      rate: "rate-rupees",
      price: "rate-rupees",
      "unit price": "rate-rupees",
      tax: "tax-percent",
      gst: "tax-percent",
      "gst percent": "tax-percent",
      discount: "discount-percent",
    },
    requiredHeaders: [
      "source-ref",
      "customer-id",
      "issued-on",
      "due-on",
      "place-of-supply",
      "description",
      "hsn",
      "quantity",
      "rate-rupees",
      "tax-percent",
    ],
  },
  // Column count MUST match the header count exactly, otherwise the
  // header→cell mapping drifts silently (customer-note + internal-note are
  // both empty here, hence the `,,,` between place-of-supply and the
  // description). If you add a header, add a matching cell.
  exampleRows: [
    "IMP-001,{customer-uuid},2026-07-13,2026-08-12,29,,,Sample line A,6205,Nos,1,1200,18,0",
    "IMP-001,{customer-uuid},2026-07-13,2026-08-12,29,,,Sample line B,6205,Nos,2,850,18,0",
  ],
  csvFileName: "invoices-import-template.csv",
  xlsx: {
    sheetName: "Invoices",
    title: "Flint · Invoice import template",
    guidance:
      "One row = one line. Group rows into invoices via source-ref (first row wins for header fields). INR + CGST/SGST assumed.",
    columnWidths: {
      "source-ref": 14,
      "customer-id": 40,
      "issued-on": 13,
      "due-on": 13,
      "place-of-supply": 16,
      "customer-note": 28,
      "internal-note": 28,
      description: 32,
      hsn: 10,
      unit: 8,
      quantity: 11,
      "rate-rupees": 14,
      "tax-percent": 12,
      "discount-percent": 16,
    },
    textHeaders: new Set([
      "source-ref",
      "customer-id",
      "issued-on",
      "due-on",
      "place-of-supply",
      "hsn",
    ]),
    fileName: "invoices-import-template.xlsx",
  },
  buildHeader: (raw) => ({
    ...commonHeaderFields(raw),
    dueOn: (raw["due-on"] ?? "").trim(),
  }),
};

function useInvoiceImport(): GroupedImportRunner<InvoiceHeader> {
  const utils = trpc.useUtils();
  const mutation = trpc.invoices.import.useMutation();
  return {
    isPending: mutation.isPending,
    skipped: mutation.data?.skipped,
    run: (rows) => mutation.mutateAsync({ rows }),
    invalidate: () => utils.invoices.list.invalidate(),
  };
}

interface ImportInvoicesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported?: (result: ImportSummary) => void;
}

export function ImportInvoicesDialog({
  open,
  onOpenChange,
  onImported,
}: ImportInvoicesDialogProps) {
  const runner = useInvoiceImport();
  return (
    <GroupedImportDialog
      config={CONFIG}
      runner={runner}
      open={open}
      onOpenChange={onOpenChange}
      onImported={onImported}
    />
  );
}
